import { useQuery } from "@tanstack/react-query";
import type { SearchRecord } from "@/lib/global-search";

export interface GlobalSearchSourceState {
  data: SearchRecord[];
  isLoading: boolean;
  error: Error | null;
}

function asRecords(value: unknown): SearchRecord[] {
  if (Array.isArray(value)) return value.filter((item): item is SearchRecord => !!item && typeof item === "object");
  if (value && typeof value === "object") {
    const nested = (value as { data?: unknown; items?: unknown }).data ?? (value as { items?: unknown }).items;
    if (Array.isArray(nested)) return nested.filter((item): item is SearchRecord => !!item && typeof item === "object");
  }
  throw new Error("Search source returned an unexpected response");
}

async function fetchRecords(url: string, signal: AbortSignal): Promise<SearchRecord[]> {
  const response = await fetch(url, { credentials: "include", signal });
  if (!response.ok) throw new Error(`Search source unavailable (${response.status})`);
  return asRecords(await response.json());
}

function useSearchSource(url: string, enabled: boolean): GlobalSearchSourceState {
  const query = useQuery<SearchRecord[]>({
    queryKey: [url],
    queryFn: ({ signal }) => fetchRecords(url, signal),
    enabled,
    staleTime: 30_000,
  });
  return {
    // Never allow disabled/denied cached data to become visible through this hook.
    data: enabled && !query.isError ? query.data ?? [] : [],
    isLoading: enabled && query.isPending,
    error: enabled ? (query.error instanceof Error ? query.error : query.error ? new Error("Search source unavailable") : null) : null,
  };
}

export function useGlobalSearch(open: boolean, query: string, allowedHrefs: ReadonlySet<string>) {
  const active = open && query.trim().length > 0;
  const canMembers = allowedHrefs.has("/app/members");
  const canVehicles = allowedHrefs.has("/app/members/vehicles");
  const members = useSearchSource(
    "/api/app/devices/members",
    active && canMembers,
  );
  const vehicles = useSearchSource("/api/app/vehicles", active && canVehicles);
  const properties = useSearchSource("/api/app/properties", active && allowedHrefs.has("/app/properties"));
  const tasks = useSearchSource("/api/app/operations/tasks", active && allowedHrefs.has("/app/tasks"));
  const supplies = useSearchSource("/api/app/supply-items", active && allowedHrefs.has("/app/supplies"));
  return {
    sources: {
      members: canMembers ? members.data : [],
      vehicles: canVehicles ? vehicles.data : [],
      properties: allowedHrefs.has("/app/properties") ? properties.data : [],
      tasks: allowedHrefs.has("/app/tasks") ? tasks.data : [],
      supplies: allowedHrefs.has("/app/supplies") ? supplies.data : [],
    },
    states: {
      members: canMembers ? members : { data: [], isLoading: false, error: null },
      vehicles: canVehicles ? vehicles : { data: [], isLoading: false, error: null },
      properties: allowedHrefs.has("/app/properties") ? properties : { data: [], isLoading: false, error: null },
      tasks: allowedHrefs.has("/app/tasks") ? tasks : { data: [], isLoading: false, error: null },
      supplies: allowedHrefs.has("/app/supplies") ? supplies : { data: [], isLoading: false, error: null },
    },
    memberAccess: canMembers,
    vehicleAccess: canVehicles,
    active,
  };
}